"use client";

/**
 * Tiny external store tracking whether the communications SSE stream is
 * connected (GIQS-162). Written by useCommunicationsEvents, read by
 * useCommunicationsSummary to drop its polling to a fallback role: while the
 * stream is live there is nothing to poll for.
 */

let connected = false;
const listeners = new Set<() => void>();

export function setCommunicationsSseConnected(value: boolean) {
  if (connected === value) return;
  connected = value;
  listeners.forEach((listener) => listener());
}

export function subscribeCommunicationsSse(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCommunicationsSseConnected() {
  return connected;
}

/** SSE `changed` hint payload. Null applicationId = unknown scope (reload). */
export interface CommunicationsChange {
  applicationId: number | null;
}

/**
 * Change-event fan-out: fired on every SSE `changed` hint so non-react-query
 * consumers (the conversations inbox, which manages threads/messages in local
 * state) can silently reload while mounted. Listeners receive the change
 * payload and skip reloads for submissions they aren't displaying.
 */
const changeListeners = new Set<(change: CommunicationsChange) => void>();

export function emitCommunicationsChanged(change: CommunicationsChange) {
  changeListeners.forEach((listener) => listener(change));
}

export function subscribeCommunicationsChanged(
  listener: (change: CommunicationsChange) => void,
) {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}
